/**
 * Textos de les restriccions dels articles (OBR-135): per què un camp està bloquejat, què es pot fer en lloc, la correcció
 * controlada d’un administrador i l’informe de l’edició massiva i de l’importador.
 * Els bloquejos i els avisos els decideix `articleEditPolicy` (@obrava/shared); aquí només hi ha el text.
 */
export const articleRestrictionsCa = {
  'backoffice.articleRestrictions': {
    // ── Estat d’ús (el bàner de la fitxa) ──────────────────────────────────────────────────────
    usageBannerE1:
      'Aquest article ja és a receptes, comandes, plantilles o altres documents: alguns camps ja no es poden canviar.',
    usageBannerE2:
      'Aquest article ja té historial (moviments, lots, pesades, recepcions o expedicions, encara que ara no en tingui existències): alguns camps ja no es poden canviar.',
    usageBannerE3:
      'Aquest article té existències i historial: alguns camps ja no es poden canviar.',

    // ── Per què un camp és bloquejat ───────────────────────────────────────────────────────────
    reasonSkuReferenced:
      'El codi no es pot canviar: l’article ja és a receptes, comandes, plantilles o altres documents, que continuarien dient el codi antic.',
    reasonSkuHistory:
      'El codi no es pot canviar: l’article ja té historial (moviments, lots, recepcions…), encara que ara no tingui existències.',
    reasonBaseUnitReferenced:
      'La unitat base no es pot canviar: l’article ja és a receptes, comandes o altres documents, que es quedarien en la unitat antiga.',
    reasonBaseUnitHistory:
      'La unitat base no es pot canviar: les existències i l’historial ja estan comptats en aquesta unitat.',
    reasonRecipeUnitRecipeLines:
      'La unitat de recepta no es pot canviar ni treure: hi ha receptes publicades que la fan servir.',
    reasonRecipeUnitMovements:
      'La unitat de recepta no es pot canviar ni treure: ja hi ha moviments o pesades registrats en aquesta unitat.',
    reasonRecipeFactorRecipeLines:
      'L’equivalència no es pot canviar: hi ha receptes publicades que fan servir aquesta unitat.',
    reasonRecipeFactorMovements:
      'L’equivalència no es pot canviar: ja hi ha moviments o pesades registrats en aquesta unitat.',
    reasonTypeIncompatible:
      'No es pot canviar la tipologia: aquest canvi altera com es compra o s’elabora l’article, i ja s’ha fet servir.',
    reasonTypePublishedRecipe:
      'No es pot passar entre matèria primera i producte auxiliar: l’article és ingredient d’una recepta publicada.',
    typeUnavailable:
      'Algunes tipologies no es poden triar: l’article ja s’ha fet servir i només pot passar a una de compatible (semielaborat ↔ producte acabat, matèria primera ↔ producte auxiliar).',

    // ── Avisos (no bloquegen: demanen confirmació en desar) ────────────────────────────────────
    warnRecipeUnit:
      'Aquest article ja és a documents o té historial. Canviar o treure la unitat de recepta pot afectar receptes en esborrany.',
    warnArticleType:
      'Aquest article ja s’ha fet servir. Passar entre matèria primera i producte auxiliar canvia com es declara a la fitxa i a l’etiqueta.',
    pendingBlockedIntro: 'Aquest desat no es pot fer:',
    warnNeedsConfirm: 'Ho hauràs de confirmar en desar.',
    confirmWarningsTitle: 'Confirma el canvi',
    confirmWarningsIntro: 'Aquest desat inclou canvis en un article que ja s’ha fet servir:',
    confirmWarningsConfirm: 'Desa igualment',

    // ── Què es pot fer en lloc ─────────────────────────────────────────────────────────────────
    alternativeNewArticle: 'Si cal, crea un article nou i dona de baixa l’antic.',
    alternativeCorrection:
      'Un administrador pot fer-ne una correcció controlada, amb motiu.',

    // ── Correcció controlada (administrador) ───────────────────────────────────────────────────
    correctionAction: 'Correcció controlada',
    correctionTitleSku: 'Correcció controlada del codi',
    correctionTitleType: 'Correcció controlada de la tipologia',
    correctionIntro:
      'Només per corregir un error. Es desa al registre d’activitat amb el motiu i qui l’ha feta. Els documents i l’historial que ja existeixen no es modifiquen.',
    correctionSkuLabel: 'Codi nou',
    correctionTypeLabel: 'Tipologia nova',
    correctionReasonLabel: 'Motiu (obligatori)',
    correctionReasonHint: 'Per què cal corregir-ho?',
    correctionConfirm: 'Corregeix',
    correctionDone: 'Correcció feta i registrada.',
    correctionUnsavedHint: 'Desa primer els canvis de la fitxa: la correcció es fa a l’instant i els recarregaria.',
    correctionErrorForbidden: 'Només un administrador pot fer una correcció controlada.',
    correctionErrorReasonRequired: 'Escriu el motiu.',
    correctionErrorValueRequired: 'Escriu el valor nou.',
    correctionErrorValueInvalid: 'El valor no és vàlid.',
    correctionErrorInUse:
      'La tipologia d’aquest article no es pot corregir: és el resultat d’una recepta o té lots de producte acabat o ordres de producció. Crea un article nou i dona de baixa l’antic.',
    correctionErrorSkuInvalid:
      'El codi no és vàlid: com a molt 64 caràcters, d’una sola línia, sense tabuladors ni espais al principi o al final.',
    correctionTypeInUse:
      'La tipologia d’aquest article no es pot corregir: és el resultat d’una recepta o té lots de producte acabat o ordres de producció.',
    correctionErrorUnchanged: 'El valor nou és igual que l’actual.',
    correctionErrorRecipeUnit:
      'Treu primer la unitat de recepta: només una matèria primera en pot tenir.',
    correctionErrorNotFound: 'No s’ha trobat l’article (potser s’ha donat de baixa).',
    correctionErrorDuplicate: 'Ja hi ha un altre article amb aquest codi.',
    correctionErrorGeneric: 'No s’ha pogut fer la correcció.',

    // ── El servidor ho ha rebutjat (algú altre ha usat l’article mentre editaves) ──────────────
    serverLocked:
      'El servidor no ha deixat fer el canvi de «{{field}}»: l’article ja s’ha fet servir. Recarrega la fitxa per veure’n l’estat.',
    fieldSku: 'Codi',
    fieldBaseUnit: 'Unitat base',
    fieldRecipeUnit: 'Unitat de recepta',
    fieldRecipeUnitFactor: 'Equivalència de la unitat de recepta',
    fieldArticleType: 'Tipologia',

    // ── Edició massiva ─────────────────────────────────────────────────────────────────────────
    bulkPreview_one:
      '{{count}} dels articles seleccionats no es podrà canviar perquè ja s’ha fet servir. Es detallarà en acabar; la resta s’actualitzarà.',
    bulkPreview_other:
      '{{count}} dels articles seleccionats no es podran canviar perquè ja s’han fet servir. Es detallarà en acabar; la resta s’actualitzarà.',
    bulkPreviewLoading: 'Mirant quins articles es poden canviar…',
    bulkReportTitle: 'Resultat de l’edició massiva',
    bulkReportSummary_one: '{{done}} de {{total}} article actualitzat; {{count}} no s’ha pogut canviar.',
    bulkReportSummary_other:
      '{{done}} de {{total}} articles actualitzats; {{count}} no s’han pogut canviar.',
    bulkReportBlockedHeading: 'No s’han canviat',
    bulkReportBlockedNote:
      'Aquests articles ja s’han fet servir i el canvi no es permet. La resta s’ha actualitzat; cap altre article no s’ha tocat.',
    bulkReportNothingDone: 'Cap article s’ha pogut canviar.',
    bulkReportClose: 'Tanca',

    // ── Importador ─────────────────────────────────────────────────────────────────────────────
    importRowBlocked: 'No s’importarà aquesta fila: {{reason}}',
    importRowWarning: 'Avís: {{reason}}',
  },
} as const;
